import { FC, useMemo, useState } from "react";
import Header from "components/range-consumption/header";
import CarDropdown from "components/range-consumption/car-dropdown";
import {
  Car,
  DrivingStyles,
  DrivingFactor,
  RoadsStatus,
  Charger,
} from "shared-types";
import { defaultCar } from "constants/cars";
import {
  acFlow,
  acvalue,
  calculateChargingTime,
  roadTypeEff,
} from "helpers/calculations";
import {
  Tab,
  TabContainer,
  AppsWrapper,
} from "components/styled-components/generic";
import { defaultCharger } from "constants/chargers";
import { RangePage } from "components/range-consumption/";
import BatteryPage from "components/battery-charge";
import { WltpOverview } from "components/range-consumption/wlt-overview";

export const RangeCalculator: FC = () => {
  const [selectedApp, setSelectedApp] = useState<"range" | "battery">("range");
  const [carToShow, setCarToShow] = useState<Car>(defaultCar);
  const [isAcOn, setIsAcOn] = useState(true);
  const [temperature, setTemperature] = useState([10]);
  const [cityRoads, setCityRoads] = useState<RoadsStatus>({
    isChosen: true,
    percentage: 100,
  });
  const [countryRoads, setCountryRoads] = useState<RoadsStatus>({
    isChosen: false,
    percentage: 0,
  });
  const [highwayRoads, setHighwayRoads] = useState<RoadsStatus>({
    isChosen: false,
    percentage: 0,
  });
  const [drivingStyle, setDrivingStyle] = useState<DrivingStyles>("normal");
  const [drivingFactor, setDrivingFactor] = useState<DrivingFactor>(1);

  const [batteryLevel, setBatteryLevel] = useState<number[]>([10, 100]);
  const [selectedCharger, setSelectedCharger] =
    useState<Charger>(defaultCharger);

  const batteryChargingTime = useMemo(() => {
    if (selectedCharger) {
      return calculateChargingTime(
        batteryLevel[0],
        batteryLevel[1],
        selectedCharger,
        carToShow
      );
    }
  }, [batteryLevel, selectedCharger, carToShow]);

  const roadsEfficency = useMemo(() => {
    return roadTypeEff(countryRoads, cityRoads, highwayRoads);
  }, [countryRoads, cityRoads, highwayRoads]);

  const range = useMemo(() => {
    return Math.floor(
      (carToShow.power /
        (carToShow.consumption *
          roadsEfficency *
          acFlow(temperature[0], acvalue(isAcOn)) *
          drivingFactor)) *
        100
    );
  }, [carToShow, temperature, isAcOn, roadsEfficency, drivingFactor]);

  const usage = useMemo(() => {
    return +(
      carToShow.consumption *
      roadsEfficency *
      acFlow(temperature[0], acvalue(isAcOn)) *
      drivingFactor
    ).toFixed(1);
  }, [carToShow, temperature, isAcOn, drivingFactor, roadsEfficency]);

  return (
    <>
      <AppsWrapper>
        <Header>Fahrzeug</Header>
        <CarDropdown setCarToShow={(car) => setCarToShow(car)} />
        <WltpOverview selectedModel={carToShow} />
        <Tabs
          carToShow={carToShow}
          selectedApp={selectedApp}
          setSelectedApp={setSelectedApp}
        />
        {selectedApp === "range" ? (
          <RangePage
            selectedModel={carToShow}
            setIsAcOn={setIsAcOn}
            isAcOn={isAcOn}
            temperature={temperature}
            setTemperature={setTemperature}
            setCountryRoads={setCountryRoads}
            countryRoads={countryRoads}
            setHighwayRoads={setHighwayRoads}
            highwayRoads={highwayRoads}
            setCityRoads={setCityRoads}
            cityRoads={cityRoads}
            usage={usage}
            range={range}
            setDrivingStyle={setDrivingStyle}
            drivingStyle={drivingStyle}
            setDrivingFactor={setDrivingFactor}
            drivingFactor={drivingFactor}
          />
        ) : (
          <BatteryPage
            carToShow={carToShow}
            batteryLevel={batteryLevel}
            setBatteryLevel={setBatteryLevel}
            batteryChargingTime={batteryChargingTime}
            selectedCharger={selectedCharger}
            setSelectedCharger={setSelectedCharger}
            range={range}
          />
        )}
      </AppsWrapper>
    </>
  );
};

export interface TabsProps {
  carToShow: Car;
  selectedApp: "range" | "battery";
  setSelectedApp: (app: "range" | "battery") => void;
}

export const Tabs: FC<TabsProps> = ({
  carToShow,
  selectedApp,
  setSelectedApp,
}) => {
  function toggleTabs(target: "range" | "battery") {
    setSelectedApp(target);
  }

  return (
    <TabContainer disabled={carToShow.id === defaultCar.id}>
      <Tab
        isActive={selectedApp === "range"}
        onClick={() => toggleTabs("range")}
      >
        Reichweitenrechner
      </Tab>
      <Tab
        isActive={selectedApp === "battery"}
        onClick={() => toggleTabs("battery")}
      >
        Ladezeitrechner
      </Tab>
    </TabContainer>
  );
};
